/** The compound-tag warning band, and the split editor it opens.
 *
 *  A tag like "109-DA/DB" is one drawing symbol standing for several plant
 *  assets, which the register can only store as one asset that exists nowhere in
 *  the plant. This is the one editor for the split, mounted wherever a reviewer
 *  meets a compound tag: the equipment edit panel, the drawing's review table,
 *  and the plant hierarchy. It proposes the parts, validates them and hands them
 *  to the caller — the writing itself belongs to `splitCompoundEquipment`.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import TagAutocompleteInput from './TagAutocompleteInput';
import { useEquipmentTypes } from '../hooks/useEquipmentTypes';
import { expandCompoundTag, isCompoundTag } from '../utils/compoundTag';
import { apiErrorMessage } from '../utils/apiError';
import { normalizeTagForSplitComparison } from '../utils/splitEquipment';
import type { CompoundSplitPart } from '../utils/splitEquipment';

/** One asset a compound tag is being split into, as the editor holds it. */
interface CompoundSplitRow {
  tag: string;
  description: string;
}

/** How wide the popover's editor is, and how far it is kept from the viewport edge. */
const POPOVER_WIDTH = 320;
const POPOVER_VIEWPORT_MARGIN = 8;

/** The split editor's opening rows: one asset per part the tag names, each
 *  starting from the description the parts shared as one symbol. */
const proposeSplitRows = (compoundTag: string, description: string): CompoundSplitRow[] => {
  const proposedTags = expandCompoundTag(compoundTag);
  return (proposedTags.length > 0 ? proposedTags : [compoundTag.trim()]).map((tag) => ({
    tag,
    description,
  }));
};

export interface CompoundSplitEditorProps {
  /** The drawing the split writes to, and whose plant the tag inputs suggest. */
  docId: string | null | undefined;
  /** The equipment being split. A different one reseeds the editor. */
  equipmentId: string;
  /** The compound tag, and the description its parts start from. */
  tag: string;
  description: string;
  /**
   * The stored equipment type the new assets would inherit — the saved value,
   * not one a reviewer has typed but not committed, because that is what the
   * split sends. The create route admits reviewed types only, so an unreviewed
   * one is caught here rather than half-way through the split.
   */
  inheritedType: string | null;
  /** True where the surface will not run the split at all, whatever the parts. */
  blocked?: boolean;
  /** What to tell the reviewer about `blocked`, where there is something to say. */
  blockedReason?: string | null;
  /** True while the surface has another save in flight for this equipment. */
  busy?: boolean;
  /** Opens with the parts already proposed, for a reviewer who came here to split. */
  openOnMount?: boolean;
  /**
   * Writes the split. Rejecting shows the reason inside the editor and leaves
   * the parts as the reviewer left them, so a failed run can be retried.
   */
  onConfirm: (parts: CompoundSplitPart[]) => Promise<void>;
  /** Called when the reviewer backs out, after the editor has closed itself. */
  onCancel?: () => void;
}

const CompoundSplitEditor: React.FC<CompoundSplitEditorProps> = ({
  docId,
  equipmentId,
  tag,
  description,
  inheritedType,
  blocked = false,
  blockedReason = null,
  busy = false,
  openOnMount = false,
  onConfirm,
  onCancel,
}) => {
  const equipmentTypes = useEquipmentTypes();
  // `null` keeps the editor closed — the reviewer is shown the warning band
  // until they ask for the split.
  const [rows, setRows] = useState<CompoundSplitRow[] | null>(
    openOnMount ? proposeSplitRows(tag, description) : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isSplitting, setIsSplitting] = useState(false);

  // A different equipment is being split, so any half-filled parts are not its
  // own. The tag and description are read as they stand when that happens
  // rather than tracked, so editing them under an open editor leaves the
  // reviewer's parts alone.
  useEffect(() => {
    setRows(openOnMount ? proposeSplitRows(tag, description) : null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId, openOnMount]);

  // The split creates the later parts through the create route, which admits
  // reviewed types only. An unreviewed stored type would let the rename commit
  // and then reject the first new asset, leaving the split half applied.
  const inheritedTypeIsUnreviewed =
    equipmentTypes.loaded &&
    Boolean(inheritedType) &&
    !equipmentTypes.types.some((item) => item.key === inheritedType);

  /** The one gate both doors into the editor pass: the band's own button, and a
   *  caller that opens the parts on mount. */
  const splitBlocked = blocked || inheritedTypeIsUnreviewed;
  const splitBlockedReason =
    blockedReason ?? (inheritedTypeIsUnreviewed ? 'Pick a reviewed type before splitting.' : null);
  const controlsDisabled = isSplitting || busy;

  const proposedTags = expandCompoundTag(tag);

  const openEditor = () => {
    setError(null);
    setRows(proposeSplitRows(tag, description));
  };

  const closeEditor = () => {
    setRows(null);
    setError(null);
    onCancel?.();
  };

  const updateRow = (index: number, patch: Partial<CompoundSplitRow>) => {
    setRows((prev) => (prev ? prev.map((row, i) => (i === index ? { ...row, ...patch } : row)) : prev));
  };

  const removeRow = (index: number) => {
    setRows((prev) => (prev ? prev.filter((_, i) => i !== index) : prev));
  };

  const addRow = () => {
    setRows((prev) => (prev ? [...prev, { tag: '', description: '' }] : prev));
  };

  const confirmSplit = async () => {
    if (!rows || splitBlocked) return;
    setIsSplitting(true);
    setError(null);
    try {
      await onConfirm(
        rows.map((row) => ({
          equipment_no: row.tag.trim(),
          description: row.description.trim(),
        })),
      );
      setRows(null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setIsSplitting(false);
    }
  };

  const normalizedTags = (rows ?? []).map((row) => normalizeTagForSplitComparison(row.tag));
  const hasEmptyTag = normalizedTags.some((partTag) => !partTag);
  const hasDuplicateTag = new Set(normalizedTags).size !== normalizedTags.length;
  // A part that is still compound would just be flagged again on publish.
  const hasCompoundTag = (rows ?? []).some((row) => isCompoundTag(row.tag));
  const splitRejection = hasEmptyTag
    ? 'Every part needs a tag.'
    : hasDuplicateTag
      ? 'Two parts name the same asset.'
      : hasCompoundTag
        ? 'A part still names more than one asset.'
        : null;

  return (
    <div
      style={{
        padding: 10,
        background: '#fffbeb',
        border: '1px solid #fcd34d',
        borderRadius: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.5 }}>
        ⚠ Compound tag — this looks like {proposedTags.length}{' '}
        {proposedTags.length === 1 ? 'asset' : 'assets'}. Splitting is recommended before
        publishing.
      </div>

      {inheritedTypeIsUnreviewed && (
        <div style={{ fontSize: 10, color: '#92400e', lineHeight: 1.5 }}>
          Pick a reviewed type for this equipment and save before splitting — the new assets
          must carry a reviewed type.
        </div>
      )}

      {/* A blocked equipment shows the closed band whichever door was used: a
          caller that seeds the parts on mount leaves them behind the same gate
          the button obeys until the block is cleared. */}
      {!rows || splitBlocked ? (
        <button
          type="button"
          onClick={openEditor}
          /* The split rewrites the tag, so only the drawing that owns the
             asset's canonical fields may run it. */
          disabled={splitBlocked || controlsDisabled}
          title={splitBlockedReason ?? undefined}
          style={{
            alignSelf: 'flex-start',
            padding: '4px 10px',
            fontSize: 11,
            fontWeight: 600,
            color: '#92400e',
            background: 'rgba(146, 64, 14, 0.08)',
            border: '1px solid rgba(146, 64, 14, 0.25)',
            borderRadius: 4,
            cursor: splitBlocked || controlsDisabled ? 'not-allowed' : 'pointer',
            opacity: splitBlocked || controlsDisabled ? 0.55 : 1,
          }}
        >
          Split into separate assets…
        </button>
      ) : (
        <div
          /* These inputs can sit inside the mark form, where a stray Enter would
             submit the mark and take the half-filled split down with it. */
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {rows.map((row, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                padding: 8,
                background: 'white',
                border: '1px solid #fde68a',
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: '#92400e',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {index === 0 ? 'this equipment, renamed' : 'new equipment'}
                </span>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    disabled={controlsDisabled}
                    style={{
                      padding: '2px 7px',
                      fontSize: 11,
                      color: '#6b7280',
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 4,
                      cursor: controlsDisabled ? 'not-allowed' : 'pointer',
                    }}
                    title="Remove this part"
                    aria-label="Remove this part"
                  >
                    ×
                  </button>
                )}
              </div>
              <TagAutocompleteInput
                value={row.tag}
                onChange={(next) => updateRow(index, { tag: next })}
                docId={docId}
                kind="equipment"
                disabled={controlsDisabled}
                placeholder="Tag"
                style={{ fontSize: 11, padding: 6 }}
              />
              <input
                type="text"
                value={row.description}
                onChange={(e) => updateRow(index, { description: e.target.value })}
                placeholder="Description"
                disabled={controlsDisabled}
                style={{
                  padding: 6,
                  fontSize: 11,
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                }}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={addRow}
            disabled={controlsDisabled}
            style={{
              alignSelf: 'flex-start',
              padding: '2px 8px',
              fontSize: 9,
              fontWeight: 500,
              color: '#92400e',
              background: 'rgba(146, 64, 14, 0.08)',
              border: '1px solid rgba(146, 64, 14, 0.25)',
              borderRadius: 4,
              cursor: controlsDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            + Add part
          </button>

          <div style={{ fontSize: 10, color: '#92400e', lineHeight: 1.5 }}>
            Every part keeps this drawing symbol, its instruments and its connections. A part
            is defined by this drawing only where no other drawing defines that tag yet.
          </div>

          {error && (
            <div style={{ fontSize: 11, color: '#b91c1c', lineHeight: 1.5 }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={closeEditor}
              disabled={controlsDisabled}
              style={{
                padding: '5px 12px',
                fontSize: 11,
                color: '#374151',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                cursor: controlsDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmSplit}
              disabled={controlsDisabled || splitBlocked || splitRejection !== null}
              title={splitBlockedReason ?? splitRejection ?? undefined}
              style={{
                padding: '5px 12px',
                fontSize: 11,
                fontWeight: 600,
                color: 'white',
                background: '#b45309',
                border: '1px solid #b45309',
                borderRadius: 4,
                cursor:
                  controlsDisabled || splitBlocked || splitRejection ? 'not-allowed' : 'pointer',
                opacity: controlsDisabled || splitBlocked || splitRejection ? 0.55 : 1,
              }}
            >
              {isSplitting ? 'Splitting…' : 'Split assets'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompoundSplitEditor;

export interface CompoundSplitTriggerProps
  extends Omit<CompoundSplitEditorProps, 'openOnMount' | 'onCancel'> {
  /** The trigger button's own look, which each table owns. */
  triggerClassName?: string;
  triggerStyle?: React.CSSProperties;
  /**
   * Disables the trigger itself, before the popover ever opens — distinct from
   * `blocked`, which lets the popover open but disables the split inside it.
   */
  triggerDisabled?: boolean;
  /** What the trigger says on hover. */
  title?: string;
}

/**
 * The `Split →` button a table places beside a compound tag, and the split
 * editor it opens over the row. The editor is positioned against the viewport
 * rather than the trigger's own box, because both tables scroll inside a
 * clipping container.
 */
export const CompoundSplitTrigger: React.FC<CompoundSplitTriggerProps> = ({
  triggerClassName,
  triggerStyle,
  triggerDisabled = false,
  title = 'Compound tag — split it into separate assets',
  onConfirm,
  ...editorProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const disabled = triggerDisabled || editorProps.busy === true;

  // The trigger travels with its row, so the editor is repinned to it whenever
  // anything the row sits in scrolls or resizes.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const repin = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const left = Math.max(
        POPOVER_VIEWPORT_MARGIN,
        Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - POPOVER_VIEWPORT_MARGIN),
      );
      setAnchor({ top: rect.bottom + 4, left });
    };
    repin();
    window.addEventListener('scroll', repin, true);
    window.addEventListener('resize', repin);
    return () => {
      window.removeEventListener('scroll', repin, true);
      window.removeEventListener('resize', repin);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        title={title}
        style={{
          ...triggerStyle,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        Split →
      </button>
      {isOpen && anchor && (
        <div
          ref={popoverRef}
          role="dialog"
          style={{
            position: 'fixed',
            top: anchor.top,
            left: anchor.left,
            width: POPOVER_WIDTH,
            maxHeight: '60vh',
            overflowY: 'auto',
            zIndex: 1500,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.22)',
            padding: 8,
          }}
        >
          <CompoundSplitEditor
            {...editorProps}
            openOnMount
            onCancel={() => setIsOpen(false)}
            onConfirm={async (parts) => {
              await onConfirm(parts);
              setIsOpen(false);
            }}
          />
        </div>
      )}
    </>
  );
};
